import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/api/client";
import type { DashboardOverview } from "@/api/types";

const KEY = ["dashboard", "overview"] as const;

/** Single-shot snapshot for the Dashboard landing screen — backed by
 * GET /api/dashboard/overview. ``useRefreshDashboard`` invalidates
 * this query on success so the table reflects the latest dates as
 * soon as the refresh job finishes. */
export function useDashboardOverview() {
  return useQuery<DashboardOverview>({
    queryKey: KEY,
    queryFn: () => apiRequest("/api/dashboard/overview"),
  });
}

interface RefreshSummaryItem {
  site_id: string;
  display_id: number;
  site_name: string | null;
  latest_encounter_date: string | null;
  latest_notes_date: string | null;
  error: string | null;
}

export interface RefreshSummary {
  refreshed_at: string;
  sites: RefreshSummaryItem[];
}

/** Admin-only: re-runs the Athena refresh job and re-fetches the
 * overview so the UI flips to the new dates immediately. */
export function useRefreshDashboard() {
  const qc = useQueryClient();
  return useMutation<RefreshSummary, Error, void>({
    mutationFn: () =>
      apiRequest("/api/dashboard/admin/refresh", { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
